import {
  Row,
  Column,
  ColumnDef,
  flexRender,
  CellContext,
  useReactTable,
  getCoreRowModel
} from '@tanstack/react-table';
import { useMemo, useState, useCallback } from 'react';

import infoIcon from 'assets/icons/info.svg';
import DownIcon from 'assets/icons/down.svg';
import { EditableCell } from './EditableCell';
import { CellMeta, RowWithId } from './types';
import CustomTooltip from 'components/Tooltip';
import DownWhiteIcon from 'assets/icons/down-white.svg';
import infoWhiteIcon from 'assets/icons/info-white.svg';

export type Cell<T, K extends keyof T> = CellContext<T, K>;

type EditableTableProps<T, K extends keyof T> = {
  data: T[];
  columns: ColumnDef<T, K>[];
  handleData: (cell: Cell<T, K>, value: T[K]) => void;
  hasError: (cell: Cell<T, K>, isHighlighted: boolean) => boolean;
};

export const EditableTable = <T, K extends keyof T>({
  data,
  columns,
  hasError,
  handleData
}: EditableTableProps<T, K>) => {
  const [initialData] = useState<T[]>(() => data);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const isDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;

  const handleAccordionClick = (rowId: string) => {
    setExpanded(prev => ({ ...prev, [rowId]: !prev?.[rowId] }));
  };

  const displayRows = useMemo(() => {
    let rowIdx = 0;
    const rows: Array<{
      row: T;
      isHeader?: boolean;
      isAccordion?: boolean;
      isSeparator?: boolean;
    }> = [];

    while (rowIdx < data.length) {
      const row = data[rowIdx];
      const meta = (row as { meta: CellMeta })['meta'];

      if (meta?.isAccordion && meta?.accordionChildrenCount) {
        if (expanded[(row as RowWithId).id]) {
          for (
            let childRowIdx = 1;
            childRowIdx <= meta.accordionChildrenCount;
            childRowIdx++
          ) {
            if (data[rowIdx + childRowIdx]) {
              rows.push({
                row: data[rowIdx + childRowIdx],
                isHeader: meta?.isHeader,
                isSeparator: meta?.isSeparator
              });
            }
          }

          rows.push({
            row,
            isAccordion: true,
            isHeader: meta?.isHeader,
            isSeparator: meta?.isSeparator
          });
        } else {
          rows.push({
            row,
            isAccordion: true,
            isHeader: meta?.isHeader,
            isSeparator: meta?.isSeparator
          });
        }

        rowIdx += meta.accordionChildrenCount + 1;
      } else {
        rows.push({
          row,
          isHeader: meta?.isHeader,
          isSeparator: meta?.isSeparator
        });
        rowIdx++;
      }
    }

    return rows;
  }, [data, expanded]);

  const handleBlur = useCallback(
    (value: T[K], cell: Cell<T, K>) => {
      if (
        (value === 0 && cell.getValue() && Number(cell.getValue()) === 0) ||
        (value !== 0 &&
          value !== null &&
          Number(value) === Number(cell.getValue())) ||
        (value === null && cell.getValue() === null)
      ) {
        return;
      }

      handleData(cell, value);
    },
    [handleData]
  );

  const adjustedColumns = useMemo(() => {
    return columns.map(col => ({
      ...col,
      cell: (cell: Cell<T, K>) => {
        const meta = (cell.row.original as { meta: CellMeta })['meta'];

        const initialValue: T[K] = (
          initialData?.find(
            item =>
              (item as RowWithId).id === (cell.row.original as RowWithId).id
          ) as T
        )[(col as { accessorKey: string }).accessorKey as K];

        return col.meta?.editable === false ? (
          <div className='flex items-center gap-2'>
            <p
              className={`text-sm${meta?.isBoldTitle ? ' font-semibold' : ''}`}
            >
              {(cell?.getValue() as string) ?? ''}
            </p>

            {!!meta?.formula && (
              <CustomTooltip
                id='chartInfo'
                content={meta?.formula}
                icon={isDarkMode ? infoWhiteIcon : infoIcon}
              />
            )}
          </div>
        ) : (
          <EditableCell
            cell={cell}
            hasError={hasError}
            handleBlur={handleBlur}
            initialValue={initialValue}
          />
        );
      }
    }));
  }, [columns, hasError, handleBlur, isDarkMode, initialData]);

  const table = useReactTable({
    data,
    columns: adjustedColumns,
    columnResizeMode: 'onChange',
    getCoreRowModel: getCoreRowModel()
  });

  return (
    <table className='min-w-full text-sm rounded-t-xl overflow-hidden'>
      <thead className='bg-slate-100'>
        {table.getHeaderGroups().map(hg => (
          <tr key={hg.id} className='bg-[#DDDDDD] border border-[#DDDDDD]'>
            {hg.headers.map(header => (
              <th
                key={header.id}
                className='p-4 text-left dark:text-gray-700'
                style={{
                  width: header.column.getSize(),
                  minWidth: header.column.columnDef.minSize
                }}
              >
                {flexRender(
                  header.column.columnDef.header,
                  header.getContext()
                )}
              </th>
            ))}
          </tr>
        ))}
      </thead>

      <tbody>
        {displayRows.map(({ row, isHeader, isAccordion, isSeparator }) => {
          const aRow = row as RowWithId;
          const meta = (row as { meta: CellMeta })['meta'];

          if (isHeader) {
            return (
              <tr
                key={aRow?.id}
                className='w-full h-10 bg-[#DDDDDD] border border-[#DDDDDD] dark:text-[#5C5C5C]'
              >
                <td
                  colSpan={columns.length}
                  className='px-4 font-semibold text-sm'
                >
                  {aRow?.title}
                </td>
              </tr>
            );
          }

          if (isAccordion) {
            return (
              <tr
                key={aRow?.id}
                onClick={() => handleAccordionClick(aRow?.id)}
                className='w-full h-10 text-center bg-[#DDDDDD] border border-[#DDDDDD] cursor-pointer select-none dark:text-[#5C5C5C]'
              >
                <td colSpan={columns.length}>
                  <div className='flex items-center justify-center gap-2 px-4'>
                    <p className='font-light text-xs'>
                      {aRow?.[expanded[aRow.id] ? 'titleExpanded' : 'title']}
                    </p>

                    <img
                      alt={expanded[aRow.id] ? 'collapse' : 'expand'}
                      src={isDarkMode ? DownWhiteIcon : DownIcon}
                      className={`ease-in-out duration-300 ${
                        expanded[aRow.id] ? '-rotate-180' : ''
                      }`}
                    />
                  </div>
                </td>
              </tr>
            );
          }

          if (isSeparator) {
            return (
              <tr
                key={aRow?.id}
                className='w-full h-7 border-x border-[#DDDDDD]'
              >
                <td
                  colSpan={columns.length}
                  className='px-4 font-semibold text-sm'
                >
                  {aRow?.title}
                </td>
              </tr>
            );
          }

          return (
            <tr key={aRow?.id} className='h-10'>
              {table.getAllColumns().map((column, index) => {
                const cellContext: CellContext<T, K> = {
                  table,
                  row: {
                    original: row,
                    getVisibleCells: () =>
                      table.getAllColumns().map(col => ({
                        id: col.id,
                        column: col,
                        getValue: () => aRow[col.id],
                        getContext: () => ({
                          table,
                          row: { original: row },
                          column: col,
                          getValue: () => aRow[col.id]
                        })
                      }))
                  } as Row<T>,
                  column: column as Column<T, K>,
                  getValue: () => aRow[column.id] as any
                } as CellContext<T, K>;

                return (
                  <td
                    key={column.id}
                    className={`border border-[#DDDDDD]${
                      index === 0 ? ' px-4  bg-gray-200 text-black' : ''
                    }${index === 0 && meta?.isIndentedTitle ? ' ps-8' : ''}${
                      meta?.isItalic ? ' italic' : ''
                    }${
                      meta?.allowEdit === false
                        ? ' bg-gray-200 dark:bg-gray-850 dark:text-white'
                        : ''
                    }`}
                  >
                    {flexRender(column.columnDef.cell, cellContext)}
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};
